import { useReactiveVar } from '@apollo/client';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type React from 'react';
import { SEARCH_PARAM } from 'src/constants';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import type { CollectionItem, MediaItem } from '../types';
import { getListViewColumnStyle, getListViewFillColumnId } from './ListViewColumnSizing';
import ListViewHeader from './ListViewHeader';
import ListViewRow from './ListViewRow';
import ListViewSkeleton from './ListViewSkeleton';
import useStyles from './ListViewStyles';
import { DENSITY_CONFIGS, type HighlightTerms } from './listView.types';
import { useListViewColumns } from './useListViewColumns';
import { useListViewData } from './useListViewData';

interface ListViewProps {
  assetsData: (MediaItem | CollectionItem)[];
}

function computeHighlightTerms(): HighlightTerms {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get(SEARCH_PARAM) || '';
  const hasWords = params.getAll('include').filter((a) => !a.includes(' '));
  const hasPhrase = params
    .getAll('include')
    .filter((a) => a.includes(' '))
    .toString()
    .replace('+', ' ');
  return { searchTerm, hasWords, hasPhrase };
}

const ListViewTable: React.FC<ListViewProps> = ({ assetsData }) => {
  const { listViewDensity: density } = useReactiveVar(akomiMediaCardGridVar);
  const densityConfig = DENSITY_CONFIGS[density];
  const classes = useStyles({ density: densityConfig });

  const rows = useListViewData(assetsData);
  const columns = useListViewColumns(density);

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableSorting: false,
  });

  // Compute highlight terms once per render (URL doesn't change between rows)
  // Recompute when component re-renders (URL change triggers parent re-render via TanStack Router)
  const highlightTerms = computeHighlightTerms();

  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColumnIds = visibleColumns.map((column) => column.id);
  const fillColumnId = getListViewFillColumnId(visibleColumnIds);
  const tableMinWidth = table.getTotalSize();
  const colGroup = (
    <colgroup>
      {visibleColumns.map((column) => (
        <col key={column.id} style={getListViewColumnStyle(column.id, column.getSize(), fillColumnId)} />
      ))}
    </colgroup>
  );

  // Show skeleton if no data (loading state)
  if (assetsData.length === 0) {
    return (
      <div className={classes.tableContainer}>
        <table className={classes.table} style={{ minWidth: tableMinWidth }}>
          {colGroup}
          <thead className={classes.tableHeader}>
            <ListViewHeader table={table} classes={classes} />
          </thead>
          <tbody>
            <ListViewSkeleton table={table} density={densityConfig} classes={classes} />
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={classes.tableContainer}>
      <table className={classes.table} style={{ minWidth: tableMinWidth }}>
        {colGroup}
        <thead className={classes.tableHeader}>
          <ListViewHeader table={table} classes={classes} />
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <ListViewRow
              key={row.id}
              row={row}
              index={index}
              classes={classes}
              density={densityConfig}
              highlightTerms={highlightTerms}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ListView: React.FC<ListViewProps> = ({ assetsData }) => {
  const { listViewDensity: density } = useReactiveVar(akomiMediaCardGridVar);

  // key={density} forces TanStack Table remount on density change,
  // preventing stale column accumulation (see gotcha_tanstack_density_stale_columns)
  return <ListViewTable key={density} assetsData={assetsData} />;
};

export default ListView;
